import { useEffect, useRef, useState } from "react";
import Icon1 from "@assets/icons/Icon";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { usersApi, type MemberDocument } from "@services/api/users";
import { showNotification } from "context/notification.service";

/** Files attached to a member (stagiaire convention…). Managers only — the backend refuses others. */
export default function MemberDocuments({ userId }: { userId: string }) {
  const [docs, setDocs] = useState<MemberDocument[]>([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    usersApi.listDocuments(userId).then(setDocs).catch(() => setDocs([]));
  }, [userId]);

  const onPick = async (file?: File) => {
    if (!file) return;
    setBusy(true);
    try {
      const doc = await usersApi.uploadDocument(userId, file);
      setDocs((d) => [...d, doc]);
    } catch {
      showNotification({ type: "error", message: "Échec de l'envoi du fichier (15 Mo max)" });
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onDelete = async (doc: MemberDocument) => {
    try {
      await usersApi.deleteDocument(userId, doc.id);
      setDocs((d) => d.filter((x) => x.id !== doc.id));
    } catch {
      showNotification({ type: "error", message: "Échec de la suppression" });
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="text-h4 text-neutral-400">Documents (convention…)</div>
      {docs.map((doc) => (
        <div key={doc.id} className="flex flex-row items-center gap-2 border-1 rounded-lg px-3 py-2">
          <Icon1 name="file" size={16} />
          <a href={doc.url} target="_blank" rel="noreferrer" className="flex-1 truncate text-h4 underline">
            {doc.name}
          </a>
          <button onClick={() => onDelete(doc)} title="Supprimer" className="text-neutral-400 hover:text-red-600">
            <Icon1 name="trash" size={16} />
          </button>
        </div>
      ))}
      <input ref={inputRef} type="file" className="hidden" onChange={(e) => onPick(e.target.files?.[0])} />
      <SecondaryButton
        variant="small"
        label={busy ? "Envoi en cours..." : "Ajouter un fichier"}
        disabled={busy}
        onClick={() => inputRef.current?.click()}
      />
    </div>
  );
}
